import { Divider, Typography } from "@material-ui/core";
import { BatchFlowRequest } from "api/thorRequestModels/Events/BatchFlowRequest";
import { observer } from "mobx-react";
import React from "react";
import { useStore } from "store/StoreConfigs";
import { batchOverviewStyles } from "./BatchOverviewStyles";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";
import BatchOverviewGrid from "components/BatchOverviewGrid/BatchOverviewGrid";
import { BatchOverviewProps } from "./BatchOverviewProps";
import LynxSelect from "components/LynxSelect/LynxSelect";
import moment from "moment";

export default observer(function BatchOverview({ batch }: BatchOverviewProps) {
    const classes = batchOverviewStyles();
    const { identityStore, thorEventViewStore } = useStore();
    // const [lspOrWs, setLspOrWs] = useState("");

    const handleChange = (event: any) => {
        const request: BatchFlowRequest = {
            flowId: Number.parseInt(event.target.value),
            batchId: batch.batchId,
            customerId: identityStore.currentCustomerId,
        };

        thorEventViewStore.setProductFlow(request);
    };

    const getLaneOriginDestination = (): string => {
        const lIfo = batch.details.laneInformation;
        if (!lIfo?.originCity && !lIfo?.originCountry) {
            return "No Data";
        }

        return `${lIfo.originCity},${lIfo.originState ?? ""},${lIfo.originCountry}-${lIfo.destinationCity},${
            lIfo.destinationState ?? ""
        },${lIfo.destinationCountry}`;
    };

    return (
        <div className={classes.main}>
            <div className={classes.selectContainer}>
                <Typography variant="h5">Product Information</Typography>
                {thorEventViewStore.isFlowsLoaded ? (
                    <div className={classes.smallIndicator}>
                        <LoadingIndicator />
                    </div>
                ) : (
                    <LynxSelect
                        className={classes.productFlowSelect}
                        isMultiple={false}
                        value={batch.details.productFlow.flowId.toString()}
                        disabled={batch.details.decision !== "Pending"}
                        onChange={handleChange}
                        options={thorEventViewStore.productFlows.map((x) => ({
                            itemName: `${x.region} Product Flow ${x.flowNumber} (${x.remainingStabilityBudget} hours)`,
                            itemValue: x.flowId,
                        }))}
                    />
                )}
            </div>
            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Product type",
                        item: batch.productStorageType?.Name ?? "No Data",
                    },
                    {
                        subTitle: "Date product quarantined",
                        item: batch.dateQuarantined ?? "No Data",
                    },
                    {
                        subTitle: "Clinical Study #",
                        item: batch.details.studyNumber ?? "No Data",
                    },
                    {
                        subTitle: "Box #",
                        item: "No Data",
                    },
                    {
                        subTitle: "Product Dose",
                        item: batch.details.batchDosage,
                    },
                ]}
            />
            <Divider className={classes.divider} />

            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Was patient dosed with the product?",
                        item: batch.isPatientDosed ? "Yes" : "No",
                    },
                    {
                        subTitle: "Temperature monitor included?",
                        item: batch.productStorageType?.Name ?? "No Data",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                ]}
            />
            <Divider className={classes.divider} />

            <Typography style={{ marginTop: "60px" }} variant="h5">
                Transportation Information
            </Typography>
            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Transportation Requirements",
                        item: `${batch.details.transportationRangeLowerLimit}°C to ${batch.details.transportationRangeUpperLimit}°C`,
                    },
                    {
                        subTitle: "Origin Site ID",
                        item: batch.details.origin.site ?? batch.details.origin.locationId,
                    },
                    {
                        subTitle: "Origin Site Name",
                        item: batch.details.origin.name,
                    },
                    {
                        subTitle: "Origin Country",
                        item: batch.details.origin.country,
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                ]}
            />
            <Divider className={classes.divider} />
            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Destination Site ID",
                        item: batch.details.destination.site ?? batch.details.destination.locationId,
                    },
                    {
                        subTitle: "Destination Site Name",
                        item: batch.details.destination.name,
                    },
                    {
                        subTitle: "Destination Country",
                        item: batch.details.destination.country,
                    },
                    {
                        subTitle: "Shipper Type",
                        item: batch.details.shipperType ?? "No Data",
                    },
                    {
                        subTitle: "Mode of transport",
                        item: batch.details.modeOfTransportation ?? "No Data",
                    },
                ]}
            />
            <Divider className={classes.divider} />
            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Transportation provider",
                        item: batch.details.shippingConditionDescription ?? "No Data",
                        //  (
                        //     <LynxSelect
                        //         isMultiple={false}
                        //         value={lspOrWs}
                        //         disabled={decision !== "Pending"}
                        //         options={[
                        //             {
                        //                 itemName: "Choose an LSP or WS",
                        //                 itemValue: "",
                        //             },
                        //             {
                        //                 itemName: "LSP",
                        //                 itemValue: "LSP",
                        //             },
                        //             {
                        //                 itemName: "WS",
                        //                 itemValue: "WS",
                        //             },
                        //         ]}
                        //         onChange={(e) => {
                        //             setLspOrWs(e.target.value);
                        //         }}
                        //         margin={"-2px 0px 0px 0px"}
                        //     />
                        // ),
                    },
                    {
                        subTitle: "Clinical site",
                        item: batch.details.clinicalSiteNumber ?? "No Data",
                    },
                    {
                        subTitle: "Lane ID",
                        item: batch.details.laneInformation?.laneNumber ?? "No Data",
                    },

                    {
                        subTitle: "Lane Origin - Destination",
                        item: getLaneOriginDestination(),
                    },
                    {
                        subTitle: "Lane Status",
                        item: batch.details.laneInformation?.status ?? "No Data",
                    },
                ]}
            />
            <Divider className={classes.divider} />
            <BatchOverviewGrid
                gridXS={2}
                data={[
                    {
                        subTitle: "Shipping Date",
                        item: moment.utc(batch.details.shipmentDate).format("DD MMM YYYY") ?? "No Data",
                    },
                    {
                        subTitle: "Delivery Date",
                        item: batch.details.deliveryDate ?? "No Data",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                    {
                        subTitle: "",
                        item: "",
                    },
                ]}
            />
        </div>
    );
});
